import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Activity, Mic, FileCheck2, Bell, Sparkles, ClipboardList, ArrowRight, Check, Loader2,
  Mail, ShieldCheck, FileDown,
} from "lucide-react";

const HERO_IMAGE = "https://customer-assets-rejwkqb3.emergentagent.net/job_pathlab-hub-20"
  + "/artifacts/nofasedo_IMG_0015.jpeg";

const MODULES = [
  { icon: ClipboardList, name: "PathTraX",
    line: "Accessioning, grossing and specimen tracking",
    detail: "Every container, block and slide carries its own barcode from accession through "
            + "sign-out, with a chain of custody that holds up to inspection." },
  { icon: Mic, name: "Transcription",
    line: "Dictation and structured sign-out",
    detail: "Gross and microscopic descriptions dictated at the bench, transcribed, returned for "
            + "correction and signed out by the pathologist of record." },
  { icon: Activity, name: "Histology management",
    line: "Bench worklists, recuts and special stains",
    detail: "Embedding, cutting and staining worklists built the way a histology bench actually "
            + "runs, including recut and level requests from the reading room." },
  { icon: FileCheck2, name: "CAP accreditation",
    line: "Inspection readiness, every day",
    detail: "Checklist evidence, SOP control, proficiency testing, nonconformance and specimen "
            + "rejection logs kept continuously rather than reconstructed before an inspection." },
  { icon: Bell, name: "STAT and TAT notifications",
    line: "Nothing sits quietly past its target",
    detail: "STAT cases, turnaround breaches, frozen sections and amendments escalate to the "
            + "people responsible, in the application and on the bench." },
  { icon: Sparkles, name: "Silas, AI-assisted alerts and voice search",
    line: "Ask the lab a question, out loud",
    detail: "A spoken morning briefing, plain-language case search and alerts on bottlenecks, "
            + "expiring reagent lots and overdue instruments." },
];

const PROOF = [
  ["Chain of custody", "Container, block and slide level"],
  ["Inspection evidence", "CAP checklist items with attached proof"],
  ["Turnaround", "Measured on every case, not sampled"],
  ["Audit trail", "Every edit, every signature, permanently"],
];

const empty = { name: "", email: "", title: "", institution: "", message: "" };

const COPY = {
  demo: {
    title: "Request a demo",
    blurb: "Tell us where you work and we will arrange a walkthrough with your own workflow in mind.",
    submit: "Send request",
    note: "We use your details only to arrange the demo.",
  },
  proposal: {
    title: "Download the pilot proposal",
    blurb: "The PathQity AP pilot proposal covers scope, implementation, validation and what a "
           + "pilot asks of your laboratory. Tell us where to credit the copy.",
    submit: "Download the proposal",
    note: "We use your details only to follow up on the proposal.",
  },
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("demo");
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate("/hub", { replace: true }); }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/demo-requests", {
        ...form, source: mode === "proposal" ? "proposal-download" : "landing-page",
      });
      setSent(data.message);
      setForm(empty);
      if (data.download) {
        const a = document.createElement("a");
        a.href = data.download;
        a.download = "PathQity_AP_Pilot_Proposal.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const openForm = (which = "demo") => () => {
    setMode(which);
    setSent("");
    setError("");
    setOpen(true);
  };
  const copy = COPY[mode];

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900" data-testid="landing-page">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3">
          <img src="/logo.png" alt="PathQity" className="h-9 w-auto"
               onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
          <span className="font-display text-lg font-bold tracking-tight text-[#134e4a]">PathQity</span>
          <nav className="ml-auto flex items-center gap-2">
            <Link to="/login" data-testid="landing-signin-link"
                  className="rounded px-3 py-2 text-sm font-semibold text-slate-600 hover:text-[#134e4a]">
              Staff sign in
            </Link>
            <Button onClick={openForm("demo")} data-testid="landing-header-demo-button"
                    className="h-9 bg-[#134e4a] px-4 text-sm hover:bg-[#0f3f3c]">
              Request a demo
            </Button>
          </nav>
        </div>
      </header>

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">
              The Trax Suite by PathQity
            </p>
            <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              The modern, intelligent laboratory management platform for anatomic pathology.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">
              PathQity tracks every specimen, block and slide from accession to sign-out, keeps your
              CAP evidence current as the work happens, and surfaces the cases that need attention
              before they become turnaround failures. Built by histologists, for the bench.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button onClick={openForm("demo")} data-testid="landing-hero-demo-button"
                      className="h-12 bg-[#134e4a] px-7 text-base hover:bg-[#0f3f3c]">
                Request a demo <ArrowRight size={18} className="ml-2" />
              </Button>
              <button onClick={openForm("proposal")} data-testid="landing-hero-proposal-button"
                      className="inline-flex h-12 items-center gap-2 rounded-md border border-slate-300 px-6 text-base font-semibold text-slate-700 transition-colors hover:border-[#134e4a] hover:text-[#134e4a]">
                <FileDown size={18} /> Download the pilot proposal
              </button>
              <Link to="/login"
                    className="inline-flex h-12 items-center px-2 text-base font-semibold text-slate-600 underline-offset-4 transition-colors hover:text-[#134e4a] hover:underline">
                Staff sign in
              </Link>
            </div>
            <p className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck size={16} className="text-[#134e4a]" />
              Role-based access, complete audit trail, CAP-aligned records
            </p>
          </div>

          <figure className="relative">
            <div className="absolute -left-4 -top-4 hidden h-full w-full border-2 border-[#134e4a]/15 lg:block" />
            <img src={HERO_IMAGE} alt="Pathology slides on the bench"
                 data-testid="landing-hero-image"
                 className="relative aspect-[4/3] w-full bg-slate-200 object-cover shadow-xl" />
          </figure>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-[#134e4a] py-10 text-white">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 sm:grid-cols-2 lg:grid-cols-4">
          {PROOF.map(([label, detail]) => (
            <div key={label}>
              <p className="font-display text-base font-bold text-teal-200">{label}</p>
              <p className="mt-1 text-sm leading-relaxed text-teal-50/80">{detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 lg:py-24" id="modules">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">The Trax Suite</p>
        <h2 className="mt-3 max-w-3xl text-base font-semibold text-slate-700 md:text-lg">
          Six modules that cover the work a pathology laboratory actually does, in one record,
          under one audit trail.
        </h2>

        <div className="mt-10 grid gap-px overflow-hidden border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
          {MODULES.map(({ icon: Icon, name, line, detail }) => (
            <article key={name} className="group bg-white p-7 transition-colors hover:bg-teal-50/40"
                     data-testid={`landing-module-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={22} className="text-[#134e4a] transition-transform group-hover:-translate-y-0.5" />
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{name}</h3>
              <p className="mt-1 text-sm font-semibold text-[#134e4a]">{line}</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:py-20">
          <div className="max-w-3xl border-l-4 border-[#134e4a] pl-6">
            <p className="font-display text-xl leading-relaxed text-slate-800 md:text-2xl">
              “Laboratory software should be built by the people who have stood at the grossing
              bench. Every screen in PathQity exists because the work demanded it.”
            </p>
            <p className="mt-5 text-sm font-semibold text-slate-900">Mark H. Potts, BS, HT (ASCP)</p>
            <p className="text-sm text-slate-600">
              Founder, PathQity — thirty years in histology and anatomic pathology
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 text-center lg:py-20">
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          See it against your own workflow.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600">
          A demo runs about thirty minutes and covers accessioning, the histology bench, sign-out
          and inspection readiness. Tell us where you work and we will arrange a time.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={openForm("demo")} data-testid="landing-footer-demo-button"
                  className="h-12 bg-[#134e4a] px-7 text-base hover:bg-[#0f3f3c]">
            Request a demo <ArrowRight size={18} className="ml-2" />
          </Button>
          <button onClick={openForm("proposal")} data-testid="landing-footer-proposal-button"
                  className="inline-flex h-12 items-center gap-2 rounded-md border border-slate-300 px-6 text-base font-semibold text-slate-700 transition-colors hover:border-[#134e4a] hover:text-[#134e4a]">
            <FileDown size={18} /> Download the pilot proposal
          </button>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-900 py-12 text-slate-300" data-testid="landing-footer">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 sm:grid-cols-3">
          <div>
            <p className="font-display text-lg font-bold text-white">PathQity</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              The Trax Suite — laboratory management for anatomic pathology, built on thirty years
              of histology practice.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</p>
            <a href="mailto:Info@markpathqitymx.com" data-testid="landing-contact-email"
               className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-teal-300 hover:text-teal-200">
              <Mail size={14} /> Info@markpathqitymx.com
            </a>
            <p className="mt-2 text-sm text-slate-400">
              Mark H. Potts, BS, HT (ASCP) — Founder
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Access</p>
            <Link to="/login" className="mt-2 block text-sm text-slate-300 hover:text-white">
              Staff sign in
            </Link>
            <button onClick={openForm("demo")} className="mt-1 block text-sm text-slate-300 hover:text-white">
              Request a demo
            </button>
            <button onClick={openForm("proposal")} data-testid="landing-footer-link-proposal"
                    className="mt-1 block text-sm text-slate-300 hover:text-white">
              Download the pilot proposal
            </button>
          </div>
        </div>
        <p className="mx-auto mt-10 max-w-6xl px-5 text-xs text-slate-500">
          © {new Date().getFullYear()} PathQity. For laboratory professional use.
        </p>
      </footer>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="demo-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{copy.title}</DialogTitle>
            <DialogDescription>{copy.blurb}</DialogDescription>
          </DialogHeader>

          {sent ? (
            <div className="py-6 text-center" data-testid="demo-request-success">
              <Check size={40} className="mx-auto text-[#134e4a]" />
              <p className="mt-4 text-base font-semibold text-slate-900">{sent}</p>
              {mode === "proposal" && (
                <a href="/PathQity_AP_Pilot_Proposal.pdf" download
                   data-testid="demo-manual-download-link"
                   className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#134e4a] hover:underline">
                  <FileDown size={14} /> If the download did not start, click here
                </a>
              )}
              <Button onClick={() => setOpen(false)} data-testid="demo-request-close-button"
                      className="mt-6 block w-full bg-[#134e4a] hover:bg-[#0f3f3c] sm:mx-auto sm:w-auto sm:px-8">Close</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="demo-name">Name</Label>
                <Input id="demo-name" required value={form.name} onChange={set("name")}
                       data-testid="demo-name-input" placeholder="Jane Whitfield" />
              </div>
              <div>
                <Label htmlFor="demo-email">Work email</Label>
                <Input id="demo-email" type="email" required value={form.email} onChange={set("email")}
                       data-testid="demo-email-input" placeholder="jwhitfield@hospital.org" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="demo-title">Title</Label>
                  <Input id="demo-title" value={form.title} onChange={set("title")}
                         data-testid="demo-title-input" placeholder="Histology Supervisor" />
                </div>
                <div>
                  <Label htmlFor="demo-institution">Institution</Label>
                  <Input id="demo-institution" value={form.institution} onChange={set("institution")}
                         data-testid="demo-institution-input" placeholder="Graham Health System" />
                </div>
              </div>
              <div>
                <Label htmlFor="demo-message">Anything we should know (optional)</Label>
                <Textarea id="demo-message" rows={3} value={form.message} onChange={set("message")}
                          data-testid="demo-message-input"
                          placeholder="Case volume, current system, timing…" />
              </div>
              {error && (
                <p className="text-sm font-semibold text-red-700" data-testid="demo-request-error">{error}</p>
              )}
              <Button type="submit" disabled={busy} data-testid="demo-submit-button"
                      className="w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
                {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                {busy ? "Sending…" : copy.submit}
              </Button>
              <p className="text-center text-xs text-slate-500">{copy.note}</p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
